'use client';

import { useRef } from 'react';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { LinkButton, withPlayfairO } from './StyleConstants';
import styles from './CollectionAnimatedSection.module.css';
import { ContainerLG, CustomImage as Image } from '..';
import gsap from 'gsap';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { splitForStagger } from 'utils/splitWords';

export const CollectionAnimatedSection = () => {
  const root = useRef<HTMLDivElement>(null);
    const pinContainer = useRef<HTMLDivElement>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const colorCtaContainer = useRef<HTMLDivElement>(null);
    const searchParams = useSearchParams();
    const t = useTranslations('ring');
    const locale = useLocale();

    const descriptionCtsContainer = useRef<HTMLDivElement>(null);

    const image1 = useRef<HTMLImageElement>(null);
    const image2 = useRef<HTMLImageElement>(null);
    const image3 = useRef<HTMLImageElement>(null);

    const button1 = useRef<HTMLButtonElement>(null);
    const button2 = useRef<HTMLButtonElement>(null);
    const button3 = useRef<HTMLButtonElement>(null);

    const gsapContext = useGsapContext();

    const animationInit = () => {
      console.log(
        !root.current,
        !pinContainer.current,
        !heading.current,
        !colorCtaContainer.current,
        !descriptionCtsContainer.current,
        !image1.current,
        !image2.current,
        !image3.current,
        !button1.current,
        !button2.current,
        !button3.current,
      );
      if (
        !root.current ||
        !pinContainer.current ||
        !heading.current ||
        !colorCtaContainer.current ||
        !descriptionCtsContainer.current ||
        !image1.current ||
        !image2.current ||
        !image3.current ||
        !button1.current ||
        !button2.current ||
        !button3.current
      )
        return;

      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
          },
          scrollTrigger: {
            trigger: root.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
          },
        });

        tl.set([image1.current, image2.current, image3.current], {
          translate: '-50%, 0',
        });

        tl.to(heading.current, {
          y: -100,
          autoAlpha: 0,
          duration: 0.2,
        });

        // tl.from(
        //   colorCtaContainer.current,
        //   {
        //     y: 100,
        //     autoAlpha: 0,
        //     duration: 0.2,
        //   },
        //   '<',
        // );

        tl.fromTo(
          [image1.current, image2.current, image3.current],
          {
            scale: 2,
            // duration: 0.1,
            xPercent: '-50',
            yPercent: '50',
          },
          {
            scale: 1,
            yPercent: '-50',
            duration: 0.4,
          },
          '<',
        );

        tl.from(
          [button1.current, button2.current, button3.current],
          {
            y: 100,
            autoAlpha: 0,
            duration: 0.2,
            stagger: 0.06,
          },
          '<+0.2',
        );

        if (descriptionCtsContainer.current) {
          tl.from(
            [...descriptionCtsContainer.current.querySelectorAll('p span')],
            {
              yPercent: 100,
              autoAlpha: 0,
              duration: 0.1,
              stagger: 0.005,
              onUpdate: () => {
                console.log(window.innerWidth);
              },
            },
            '<',
          );

          tl.from(
            descriptionCtsContainer.current.querySelectorAll('a'),
            {
              yPercent: 100,
              autoAlpha: 0,
              duration: 0.2,
            },
            '<',
          );
        }

        tl.to(
          button1.current,
          {
            autoAlpha: 1,
            duration: 0.2,
          },
          '<',
        );

        tl.addPause('<+0.5');

        tl.addLabel('image1-transition');

        tl.to(image1.current, {
          clipPath: 'polygon(0 0, 100% 0, 100% 0%, 0 0%)',
          duration: 1,
        });

        tl.to(
          button1.current,
          {
            autoAlpha: 0.2,
            duration: 0.5,
          },
          '<',
        );
        tl.to(
          button2.current,
          {
            autoAlpha: 1,
            duration: 1,
          },
          '<',
        );

        tl.addLabel('image2-transition');

        tl.to(image2.current, {
          clipPath: 'polygon(0 0, 100% 0, 100% 0%, 0 0%)',
          duration: 1,
        });

        // tl.to(
        //   button1.current,
        //   {
        //     autoAlpha: 0,
        //     duration: 0.5,
        //   },
        //   '<',
        // );
        tl.to(
          button2.current,
          {
            autoAlpha: 0.2,
            duration: 0.5,
          },
          '<',
        );
        tl.to(
          button3.current,
          {
            autoAlpha: 1,
            duration: 1,
          },
          '<',
        );

        tl.addLabel('image3-transition');

        // tl.to(image3.current, {
        //   clipPath: 'polygon(0 0, 100% 0, 100% 0%, 0 0%)',
        //   duration: 1,
        // });

        // gsap context end
      });
    };

    useIsomorphicLayoutEffect(() => {
      const timeout = setTimeout(() => {
        animationInit();
      }, 100);

      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }, []);

    return (
      <section className={styles.root} ref={root} id="rare-collection">
        <div ref={pinContainer} className="pin-container">
          <ContainerLG>
            <h2 ref={heading}>
              {withPlayfairO(
                t.rich('ring.rare.collection.heading', { br: () => <br /> }),
              )}
            </h2>

            <div className="ring-image-container">
              <Image
                ref={image1}
                src="web_v2/rare/ring-render/rare-dessert-rose.png"
                alt={t('ring.rare.collection.ringAlt')}
                width="846"
                height="1097"
                style={{ zIndex: 3 }}
                loading="eager"
              />
              <Image
                ref={image2}
                src="web_v2/rare/ring-render/rare-dessert-snow.png"
                alt={t('ring.rare.collection.ringAlt')}
                width="846"
                height="1097"
                style={{ zIndex: 2 }}
                loading="lazy"
              />
              <Image
                ref={image3}
                src="web_v2/rare/ring-render/rare-dune.png"
                alt={t('ring.rare.collection.ringAlt')}
                width="846"
                height="1097"
                style={{ zIndex: 1 }}
                loading="lazy"
              />
            </div>

            <div className="color-cta-container" ref={colorCtaContainer}>
              <button ref={button1}>Desert Rose</button>
              <button ref={button2}>Desert Snow</button>
              <button ref={button3}>Dune</button>
            </div>

            <div
              className="description-cta-container"
              ref={descriptionCtsContainer}
            >
              <p>
                {/* {`Rare launches with the "Desert Collection" with three distinctive pieces: Desert Rose, Desert Snow, and Dune.`
                  .split(' ')
                  .map((word, index) => (
                    <>
                      <span key={index}>{word}&nbsp;</span>
                    </>
                  ))}
                <br />
                <br /> */}
                {splitForStagger(t('ring.rare.collection.descriptionA'), locale).map(
                  (part, index) =>
                    part.isWord ? (
                      <span key={`a-${index}`}>{part.text}</span>
                    ) : (
                      part.text
                    ),
                )}
                <br />
                <br />
                {splitForStagger(t('ring.rare.collection.descriptionB'), locale).map(
                  (part, index) =>
                    part.isWord ? (
                      <span key={`b-${index}`}>{part.text}</span>
                    ) : (
                      part.text
                    ),
                )}
                <br />
                {splitForStagger(t('ring.rare.collection.descriptionC'), locale).map(
                  (part, index) =>
                    part.isWord ? (
                      <span key={`c-${index}`}>{part.text}</span>
                    ) : (
                      part.text
                    ),
                )}
              </p>
              <LinkButton
                href={{
                  pathname: 'rare/buy',
                  query: Object.fromEntries(searchParams?.entries() ?? []),
                }}
              >
                {t('ring.rare.buyNow')}
              </LinkButton>
            </div>
          </ContainerLG>
        </div>
      </section>
    );
};
